import * as React from 'react';

import { Hint } from '../hint/hint.tsx';
import { Icon } from '../icon/icon.tsx';

import './section-card.css';

export type SectionCardIconType =
  | 'personal'
  | 'offices'
  | 'licenses'
  | 'education'
  | 'certifications'
  | 'insurance'
  | 'affiliations'
  | 'references'
  | 'work';

interface SectionCardProps extends React.HTMLAttributes<HTMLDivElement> {
  documentation?: string;
  time?: number;
  state: 'error' | 'warning' | 'default';
  progress: number;
  progressText?: string;
  name: string;
  iconType: SectionCardIconType;
  /** Replaces the progress-derived status title for a form holding an unsaved edit. */
  needsAttention?: boolean;
}

const SectionCard = React.forwardRef<HTMLDivElement, SectionCardProps>(
  (
    {
      progress,
      progressText,
      state,
      time,
      name,
      iconType,
      documentation,
      needsAttention = false,
      onClick: onCardClick,
      onKeyDown: onCardKeyDown,
      onKeyUp: onCardKeyUp,
      tabIndex: cardTabIndex,
      ...props
    },
    ref,
  ) => {
    const statusTitleRegistry = ['Not Started Yet', 'In Progress', 'Completed'];
    const stateRegistry = {
      default: 'default',
      error: 'error',
      warning: 'warning',
    };

    let statusTitle: string = statusTitleRegistry[0];

    if (progress === 0) statusTitle = statusTitleRegistry[0];
    if (progress > 0 && progress < 100) statusTitle = statusTitleRegistry[1];
    if (progress === 100) statusTitle = statusTitleRegistry[2];
    // Last, so it wins over whatever the stored-data figure implies.
    if (needsAttention) statusTitle = 'Needs attention';

    return (
      <div className="section-card-container">
        <div
          ref={ref}
          {...props}
          className={`section-card section-card-${stateRegistry[state]}`}
          {...(onCardClick ? { role: 'button' as const } : {})}
          tabIndex={cardTabIndex ?? (onCardClick ? 0 : undefined)}
          onClick={onCardClick}
          onKeyDown={e => {
            onCardKeyDown?.(e);
            if (e.defaultPrevented) return;
            if (!onCardClick) return;
            // Keys pressed on the docs link or inside its card belong to them.
            if (e.target !== e.currentTarget) return;

            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              (onCardClick as unknown as (evt: React.KeyboardEvent) => void)(e);
            }
          }}
          onKeyUp={onCardKeyUp}
        >
          <progress
            className={`progress ${progress === 100 && 'completed'}`}
            max="100"
            value={progress}
          />
          <div className="content">
            <div className="status-title">{statusTitle}</div>
            <div className="status-text">{progressText}</div>
            <hr />
            <div className="status-icon">
              <Icon
                type={iconType}
                size="xxxlarge"
                style={!documentation ? { marginTop: '15px' } : {}}
                viewBox="0 0 43 35"
              />
            </div>
            <div className="status-section">{name}</div>
            {time && state === 'default' && (
              <div className="status-time">
                <Icon type="timer" className="time-icon" />
                <span>{time}'</span>
              </div>
            )}
            {state === 'error' && (
              <Icon type="error" size="large" style={{ marginTop: '2px' }} />
            )}
            {state === 'warning' && (
              <Icon type="warning" size="large" style={{ marginTop: '2px' }} />
            )}
            {documentation && (
              // React events bubble out of the docs' portal too; stop them before they open the section.
              <div
                className="status-documentation-area"
                onClick={e => e.stopPropagation()}
              >
                <Hint
                  title="Required Documentation"
                  text={documentation}
                  align="center"
                  trigger={
                    <button type="button" className="status-documentation">
                      Required Documentation
                      <span />
                    </button>
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export { SectionCard };
